<script setup lang="ts">
import { Check, Eye, EyeOff, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const authStore = useAuthStore();

const email = ref('');
const password = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const loading = ref(false);
const error = ref('');
const requiresPasswordChange = ref(false);
const requiresAgreementAcceptance = ref(false);
const hasScrolledToBottom = ref(false);
const agreementCheckbox = ref(false);
const privacyTermsCheckbox = ref(false);
const agreementScrollContainer = ref<HTMLElement | null>(null);

// Password validation rules
const hasMinLength = computed(() => newPassword.value.length >= 8);
const hasUppercase = computed(() => /[A-Z]/.test(newPassword.value));
const hasNumber = computed(() => /\d/.test(newPassword.value));
const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value));
const passwordsMatch = computed(() => {
  if (!confirmNewPassword.value) return null;
  return newPassword.value === confirmNewPassword.value;
});

const isPasswordValid = computed(() => {
  return hasMinLength.value && hasUppercase.value && hasNumber.value && hasSpecialChar.value;
});

const canSubmitNewPassword = computed(() => {
  return isPasswordValid.value && passwordsMatch.value === true && !loading.value;
});

const canAcceptAgreement = computed(() => {
  return (
    hasScrolledToBottom.value &&
    agreementCheckbox.value &&
    privacyTermsCheckbox.value &&
    !loading.value
  );
});

// Scroll detection for service agreement
function handleAgreementScroll(event: Event) {
  const container = event.target as HTMLElement;
  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;

  // Calculate the percentage scrolled
  const scrollableHeight = scrollHeight - clientHeight;
  const scrollPercentage = scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 100;

  // Consider scrolled to bottom if user has scrolled 90% or more (10% buffer for Windows compatibility)
  if (scrollPercentage >= 90) {
    hasScrolledToBottom.value = true;
  }
}

function handleBackToLogin() {
  requiresAgreementAcceptance.value = false;
  error.value = '';
}

function handleAcceptAgreement() {
  requiresAgreementAcceptance.value = false;
  requiresPasswordChange.value = true;
}

async function handleSubmit() {
  error.value = '';
  loading.value = true;

  try {
    const result = await authStore.login(email.value, password.value);

    if (result.requiresPasswordChange) {
      requiresAgreementAcceptance.value = true;
      loading.value = false;
      return;
    }

    await completeLoginFlow();
  } catch (err: any) {
    console.error('Login failed:', err);

    if (err.message?.includes('password')) {
      error.value = 'Please contact your administrator to reset your password';
    } else if (err.message?.includes('Incorrect username or password')) {
      error.value = 'Invalid email or password';
    } else if (err.message?.includes('User does not exist')) {
      error.value = 'No account found with this email';
    } else {
      error.value = err.message || 'An error occurred during sign in';
    }
  } finally {
    loading.value = false;
  }
}

async function handleNewPasswordSubmit() {
  if (!canSubmitNewPassword.value) return;

  error.value = '';
  loading.value = true;

  try {
    // Refresh session to prevent 3-minute timeout expiry
    await authStore.login(email.value, password.value);
    password.value = ''; // Clear password from memory immediately

    await authStore.completeNewPassword(newPassword.value);
    await completeLoginFlow();
  } catch (err: any) {
    console.error('Password change failed:', err);
    error.value = err.message || 'Failed to set new password';
  } finally {
    loading.value = false;
  }
}

async function completeLoginFlow() {
  const { useOrganizationStore } = await import('@/stores/organization');
  const organizationStore = useOrganizationStore();
  await organizationStore.loadUserOrganizations();

  const redirectPath = sessionStorage.getItem('authRedirect');
  sessionStorage.removeItem('authRedirect');

  if (redirectPath) {
    router.push(redirectPath);
  } else if (organizationStore.currentOrgId) {
    router.push('/dashboard');
  } else {
    router.push('/');
  }
}
</script>

<template>
  <!-- Login Form -->
  <form
    v-if="!requiresAgreementAcceptance && !requiresPasswordChange"
    class="space-y-6"
    @submit.prevent="handleSubmit"
  >
    <div class="space-y-2">
      <Label for="email" class="text-sm font-medium"> Email address </Label>
      <Input
        id="email"
        v-model="email"
        name="email"
        type="email"
        autocomplete="email"
        required
        class="h-11 focus-visible:ring-blue-600 focus-visible:border-blue-600"
        placeholder="Enter your email"
      />
    </div>

    <div class="space-y-2">
      <Label for="password" class="text-sm font-medium"> Password </Label>
      <Input
        id="password"
        v-model="password"
        name="password"
        type="password"
        autocomplete="current-password"
        required
        class="h-11 focus-visible:ring-blue-600 focus-visible:border-blue-600"
        placeholder="Enter your password"
      />
    </div>

    <div
      v-if="error"
      class="rounded-md bg-red-50 dark:bg-red-950 p-4 border border-red-200 dark:border-red-800"
    >
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0">
          <svg
            class="h-5 w-5 text-red-600 dark:text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-red-800 dark:text-red-300">Authentication failed</h3>
          <div class="mt-1 text-sm text-red-700 dark:text-red-400">
            {{ error }}
          </div>
        </div>
      </div>
    </div>

    <div class="space-y-4 pt-2">
      <Button
        type="submit"
        :loading="loading"
        class="w-full h-11 bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-600 text-white font-semibold shadow-sm transition-all"
      >
        {{ loading ? 'Signing in...' : 'Sign in' }}
      </Button>

      <p class="text-center text-xs text-muted-foreground">
        By signing in, you agree to our
        <a
          href="https://retaintive.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          class="text-blue-600 hover:text-blue-700 hover:underline font-medium"
          >Terms of Service</a
        >
        and
        <a
          href="https://retaintive.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          class="text-blue-600 hover:text-blue-700 hover:underline font-medium"
          >Privacy Policy</a
        >
      </p>
    </div>
  </form>

  <!-- Service Agreement Acceptance -->
  <div v-else-if="requiresAgreementAcceptance && !requiresPasswordChange" class="space-y-6">
    <!-- Header -->
    <div class="text-center mb-6">
      <div class="flex justify-center mb-4">
        <img src="/logo.svg" alt="RETAINTIVE.AI" class="h-12" />
      </div>
      <h2 class="text-2xl font-bold text-gray-900">Review and Accept Service Agreement</h2>
      <p class="mt-2 text-sm text-gray-600">
        Please review our Service Agreement carefully before continuing. This agreement covers your
        use of the platform, data privacy, and AI-powered features.
      </p>
    </div>

    <!-- Scrollable Agreement Content -->
    <div
      ref="agreementScrollContainer"
      class="border border-gray-300 rounded-lg p-6 h-96 overflow-y-auto bg-white text-sm"
      @scroll="handleAgreementScroll"
    >
      <div class="prose prose-sm max-w-none">
        <h3 class="text-lg font-bold text-center mb-4">RETAINTIVE.AI SERVICE AGREEMENT</h3>

        <p class="text-sm text-gray-600 mb-4">
          <strong>Effective Date:</strong> [Date Customer Signs]<br />
          <strong>Last Updated:</strong> October 13, 2025
        </p>

        <p>
          This Service Agreement (this "Agreement") is entered into by and between Retaintive.ai,
          Inc. ("Retaintive", "we", "us") and the customer ("Customer", "you").
        </p>

        <p class="font-semibold mt-4">
          BY CLICKING "I ACCEPT" BELOW, YOU AGREE TO BE BOUND BY THIS AGREEMENT.
        </p>

        <h4 class="font-bold mt-6 mb-2">1. DEFINITIONS</h4>
        <p>
          <strong>1.1. "Services"</strong> means Retaintive.ai's cloud-based software platform that
          uses artificial intelligence to analyze customer retention data.
        </p>
        <p>
          <strong>1.2. "Customer Data"</strong> means all data provided by Customer, including call
          recordings and business data.
        </p>
        <p>
          <strong>1.3. "AI Recommendations"</strong> means suggestions generated by Retaintive's AI
          algorithms.
        </p>

        <h4 class="font-bold mt-6 mb-2">2. SERVICES AND ACCESS</h4>
        <p>
          Retaintive will provide Customer with access to the Services. Customer may access the
          Services through a web browser or mobile application.
        </p>

        <h4 class="font-bold mt-6 mb-2">3. FEES AND PAYMENT</h4>
        <p>
          <strong>Setup Deposit:</strong> $99 (one-time, due at signup)<br />
          <strong>Monthly Subscription:</strong> $299 per location per month
        </p>

        <h4 class="font-bold mt-6 mb-2">4. CUSTOMER DATA AND PRIVACY</h4>
        <p>
          Customer retains all rights to Customer Data. Retaintive will process data in accordance
          with applicable laws including CCPA and GDPR.
        </p>
        <p>
          <strong>Data Security:</strong> We implement encryption (TLS 1.2+, AES-256), regular
          security assessments, and access controls.
        </p>

        <h4 class="font-bold mt-6 mb-2">5. CALL RECORDING AND SENSITIVE INFORMATION</h4>
        <p class="text-red-600 font-semibold">
          IMPORTANT: Customer is solely responsible for obtaining all legally required consents to
          record calls.
        </p>
        <p>
          <strong>AI-Powered Redaction:</strong> Our AI attempts to detect and redact sensitive
          information (credit card numbers, SSNs) but is NOT 100% ACCURATE. Some sensitive data may
          remain.
        </p>
        <p class="font-semibold">
          Customer assumes all risk for any sensitive information in call recordings.
        </p>

        <h4 class="font-bold mt-6 mb-2">6. AI RECOMMENDATIONS</h4>
        <p>
          AI Recommendations are suggestions only, not guarantees. Retaintive does not guarantee
          business outcomes. Customer is solely responsible for business decisions based on AI
          recommendations.
        </p>

        <h4 class="font-bold mt-6 mb-2">7. WARRANTIES AND DISCLAIMERS</h4>
        <p class="font-semibold">
          THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We disclaim all implied
          warranties of MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, and NON-INFRINGEMENT.
        </p>

        <h4 class="font-bold mt-6 mb-2">8. LIMITATION OF LIABILITY</h4>
        <p class="font-semibold">
          RETAINTIVE'S TOTAL LIABILITY SHALL NOT EXCEED FEES PAID IN THE LAST 12 MONTHS.
        </p>
        <p>
          We are not liable for indirect, incidental, or consequential damages, lost profits, data
          loss, PCI-DSS fines, or regulatory penalties.
        </p>

        <h4 class="font-bold mt-6 mb-2">9. CUSTOMER RESPONSIBILITIES</h4>
        <p>Customer is responsible for:</p>
        <ul class="list-disc pl-6 mt-2">
          <li>Obtaining call recording consents</li>
          <li>Implementing access controls</li>
          <li>Training staff on data security</li>
          <li>PCI-DSS compliance if processing payment cards</li>
          <li>Not sharing login credentials</li>
        </ul>

        <h4 class="font-bold mt-6 mb-2">10. TERM AND TERMINATION</h4>
        <p>
          This Agreement continues until terminated. Either party may terminate with 30 days'
          notice. Upon termination, Customer Data will be deleted after 30 days.
        </p>

        <h4 class="font-bold mt-6 mb-2">11. GOVERNING LAW</h4>
        <p>
          This Agreement is governed by Delaware law. Disputes will be resolved in Delaware courts.
        </p>

        <p class="mt-6 font-semibold text-center">
          By accepting, you confirm you have authority to bind your organization to this Agreement.
        </p>

        <div class="mt-8 pt-6 border-t border-gray-300">
          <p class="text-center text-sm text-gray-600 font-semibold mb-4">
            — END OF SERVICE AGREEMENT —
          </p>
          <p class="text-center text-xs text-gray-500">
            This constitutes the entire agreement between Retaintive.ai and Customer.
          </p>
          <p class="text-center text-xs text-gray-500 mt-2">
            No modifications to this Agreement shall be valid unless in writing and signed by both parties.
          </p>
          <p class="text-center text-xs text-gray-500 mt-2">
            Thank you for reading the complete Service Agreement.
          </p>
          <p class="text-center text-xs text-gray-400 mt-4">
            Document Version: 1.0.0 | October 2025
          </p>
        </div>
      </div>
    </div>

    <!-- Scroll Indicator -->
    <div v-if="!hasScrolledToBottom" class="text-center text-sm text-gray-500">
      ↓ Please scroll through the agreement to continue (90% required) ↓
    </div>

    <!-- Checkboxes -->
    <div class="space-y-3">
      <label class="flex items-start gap-3 cursor-pointer">
        <input
          v-model="agreementCheckbox"
          type="checkbox"
          :disabled="!hasScrolledToBottom"
          class="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        <span class="text-sm" :class="!hasScrolledToBottom ? 'text-gray-400' : 'text-gray-700'">
          I have read and agree to the Service Agreement, including all terms regarding AI
          recommendations, call recording compliance, data privacy, and limitations of liability.
        </span>
      </label>

      <label class="flex items-start gap-3 cursor-pointer">
        <input
          v-model="privacyTermsCheckbox"
          type="checkbox"
          :disabled="!hasScrolledToBottom"
          class="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        <span class="text-sm" :class="!hasScrolledToBottom ? 'text-gray-400' : 'text-gray-700'">
          I have read and agree to the
          <a
            href="https://retaintive.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 hover:text-blue-800 hover:underline"
            >Privacy Policy</a
          >
          and
          <a
            href="https://retaintive.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 hover:text-blue-800 hover:underline"
            >Terms of Use</a
          >.
        </span>
      </label>
    </div>

    <p class="text-xs text-gray-500 text-center">
      By accepting, you confirm you have authority to bind your organization to this agreement.
    </p>

    <!-- Buttons -->
    <div class="flex gap-3">
      <Button type="button" variant="outline" class="flex-1" @click="handleBackToLogin">
        Back
      </Button>
      <Button
        type="button"
        class="flex-1"
        :disabled="!canAcceptAgreement"
        @click="handleAcceptAgreement"
      >
        I Accept
      </Button>
    </div>
  </div>

  <!-- Password Change Form -->
  <form
    v-else-if="requiresPasswordChange"
    class="space-y-6"
    @submit.prevent="handleNewPasswordSubmit"
  >
    <!-- Info Alert -->
    <Alert class="bg-blue-50 border-blue-200">
      <AlertDescription class="text-blue-800">
        <span class="font-medium">Password Change Required</span><br />
        This is your first login. Please set a new, secure password to continue.
      </AlertDescription>
    </Alert>

    <!-- New Password -->
    <div class="space-y-2">
      <Label for="new-password">New Password</Label>
      <div class="relative">
        <Input
          id="new-password"
          v-model="newPassword"
          :type="showNewPassword ? 'text' : 'password'"
          placeholder="Enter your new password"
          class="pr-10"
          :disabled="loading"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-accent rounded-md transition-colors"
          @click="showNewPassword = !showNewPassword"
        >
          <Eye v-if="!showNewPassword" class="h-4 w-4 text-muted-foreground" />
          <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>

    <!-- Confirm Password -->
    <div class="space-y-2">
      <Label for="confirm-password">Confirm New Password</Label>
      <div class="relative">
        <Input
          id="confirm-password"
          v-model="confirmNewPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="Confirm your new password"
          class="pr-10"
          :disabled="loading"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-accent rounded-md transition-colors"
          @click="showConfirmPassword = !showConfirmPassword"
        >
          <Eye v-if="!showConfirmPassword" class="h-4 w-4 text-muted-foreground" />
          <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
      <p v-if="passwordsMatch === false" class="text-sm text-destructive flex items-center gap-1.5">
        <X class="h-3.5 w-3.5" />
        Passwords do not match
      </p>
      <p
        v-else-if="passwordsMatch === true"
        class="text-sm text-green-600 flex items-center gap-1.5"
      >
        <Check class="h-3.5 w-3.5" />
        Passwords match
      </p>
    </div>

    <!-- Password Requirements -->
    <div class="space-y-2">
      <p class="text-sm font-medium text-gray-700">Your new password must contain:</p>
      <ul class="space-y-1.5 text-sm">
        <li
          :class="hasMinLength ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasMinLength" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          At least 8 characters
        </li>
        <li
          :class="hasUppercase ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasUppercase" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          An uppercase letter (A-Z)
        </li>
        <li
          :class="hasNumber ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasNumber" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          A number (0-9)
        </li>
        <li
          :class="hasSpecialChar ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasSpecialChar" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          A special character (e.g., !, @, #, $)
        </li>
      </ul>
    </div>

    <!-- Error Alert -->
    <Alert v-if="error" variant="destructive">
      <AlertDescription>{{ error }}</AlertDescription>
    </Alert>

    <!-- Submit Button -->
    <div>
      <Button type="submit" :loading="loading" :disabled="!canSubmitNewPassword" class="w-full">
        {{ loading ? 'Setting Password...' : 'Set New Password & Continue' }}
      </Button>
    </div>
  </form>
</template>
